import React, { useContext, useEffect, useState, useCallback } from 'react';
import _get from 'lodash/get';
import clsx from 'clsx';
import { history } from '../../../Routes/history';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import { makeStyles } from '@material-ui/core/styles';
import {
  UserContext,
  UserDispatchContext
} from '../../../Contexts/UserContext';
import {
  ContractsContext,
  ContractSelectedContext
} from '../../../Contexts/ContractsContext';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';
import { redirectOnAuthFailure } from '../../../Utils/Errors/Errors';
import { Container, Typography, Grid } from '@material-ui/core';
import InvoiceCard from '../../../Components/Cards/InvoiceCard/InvoiceCard';
import { GetInvoicesAPI } from '../../../API/Invoices/InvoicesAPI';
import { formatAddress } from '../../../Utils/Format/Address';
import { fullName } from '../../../Utils/Format/Names';
import { OpenNewTab } from '../../../Utils/Misc/Links';
import FlatButton from '../../../Components/Buttons/FlatButton';
import BackIcon from '../../../Components/CustomIcons/BackIcon';
import EyeIcon from '../../../Components/CustomIcons/EyeIcon';
import Skeleton from '@material-ui/lab/Skeleton';
import SimpleTabs from '../../../Views/Payment/Tabs/CustomTabs';
import InvoiceConsumptionGraph, {
  DISPLAY_UNIT
} from '../InvoiceConsumptionGraph';
import { Company } from '../../../Configs/general';
import { CONTRACT_TYPES } from '../../Contracts/contract_enums';
import { logoutUser } from '../../../Utils/User/Actions';
import IndustrialInvoiceConsumptionGraph from '../IndustrialInvoiceConsumptionGraph';

const InvoiceHistoryGDC = () => {
  const currentUser = useContext(UserContext);
  const setCurrentUser = useContext(UserDispatchContext);
  const authToken = _get(currentUser, 'token');
  const contracts = useContext(ContractsContext);

  const selectedContract = useContext(ContractSelectedContext);

  const setAlert = useContext(AlertsDispatchContext);

  const [invoices, setInvoices] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(true);
  const [currentTabIndex, setCurrentTabIndex] = useState({
    noChecked: 0,
    checked: 0
  });

  const classes = useStyles();

  useEffect(() => {
    if (!authToken) {
      history.replace('/');
      return;
    }

    if (!contracts || contracts.length === 0) {
      history.replace(ROUTE_NAMES.contracts);
      return;
    }

    let ignoreRequest = false;

    const fetchData = async () => {
      setInvoiceLoading(true);
      const contractId = selectedContract.id;
      const response = await GetInvoicesAPI(contractId, null, authToken);

      if (ignoreRequest) {
        return;
      }

      if (response.success) {
        setInvoices(response.data.data);
        setInvoiceLoading(false);
      } else {
        setInvoices(null);
        setInvoiceLoading(false);

        if (
          redirectOnAuthFailure(response, '/', () => logoutUser(setCurrentUser))
        ) {
          return;
        }
      }
    };
    fetchData();

    return () => {
      ignoreRequest = true;
    };
  }, [authToken, contracts, selectedContract, setCurrentUser, setAlert]);

  const downloadInvoice = useCallback(
    idx => {
      OpenNewTab(invoices[idx].url);
    },
    [invoices]
  );

  const payInvoice = useCallback(() => {
    history.push(ROUTE_NAMES.payments, {
      type: 'contract',
      value: selectedContract.id,
      currentStep: 1,
      disabledNext: false,
      payment: [{ type: 'contract', detail: invoices[0] }]
    });
  }, [selectedContract, invoices]);

  const renderDownloadInvoice = useCallback(
    idx => (
      <Grid item>
        <FlatButton
          color="primary"
          className={classes.actionButton}
          onClick={() => downloadInvoice(idx)}
        >
          Ver factura
          <EyeIcon size={16} className={classes.iconLeftStyle} />
        </FlatButton>
      </Grid>
    ),
    [classes.iconLeftStyle, classes.actionButton, downloadInvoice]
  );

  const renderInvoices = useCallback(() => {
    if (invoiceLoading) {
      return (
        <>
          <InvoiceCard
            className={classes.mainInvoiceCardLoading}
            loading={invoiceLoading}
          ></InvoiceCard>
          <Grid container className={classes.invoiceActions}>
            <Skeleton className={classes.invoiceActionsSkeleton} />
            <Skeleton className={classes.invoiceActionsSkeleton} />
          </Grid>
        </>
      );
    }

    if (!invoices) {
      return (
        <>
          <InvoiceCard className={classes.mainInvoiceCard} />
          <Grid container className={classes.invoiceActions}></Grid>
        </>
      );
    }

    return (
      <>
        {invoices.map((invoice, idx) => {
          const hasUrl =
            Boolean(invoice.url) && invoice.url.includes('watermark');
          const canPayInvoice = !invoice.isPaid && !invoice.isPending;
          return (
            <div key={idx}>
              <InvoiceCard
                className={clsx({
                  [classes.mainInvoiceCard]: idx === 0 || hasUrl,
                  [classes.invoiceCard]: idx !== 0 && !hasUrl
                })}
                invoiceDetails={invoice}
              />
              <Grid
                container
                className={clsx({
                  [classes.invoiceActions]: true,
                  [classes.noTopBorder]: !canPayInvoice && !hasUrl
                })}
              >
                <Grid item xs={12} sm={6} md={4}>
                  <Grid container justifyContent="space-between">
                    {hasUrl && renderDownloadInvoice(idx)}
                  </Grid>
                </Grid>
                {canPayInvoice && idx === 0 && (
                  <Grid item>
                    <FlatButton
                      color="primary"
                      className={classes.actionButton}
                      onClick={payInvoice}
                    >
                      Pagar factura
                      <BackIcon size={16} className={classes.iconRightStyle} />
                    </FlatButton>
                  </Grid>
                )}
              </Grid>
            </div>
          );
        })}
      </>
    );
  }, [invoices, classes, payInvoice, invoiceLoading, renderDownloadInvoice]);

  if (!contracts || contracts.length === 0 || !selectedContract) {
    return <></>;
  }

  if (!contracts || contracts.length === 0 || !selectedContract) {
    return <></>;
  }

  const handlerTabChange = (_event, newValue) => {
    setCurrentTabIndex({ noChecked: newValue, checked: newValue });
  };

  const tabProps = [
    {
      id: 0,
      label: 'Histórico de facturas',
      allowedContractTypes: null
    },
    {
      id: 1,
      label: 'Histórico de consumos',
      allowedContractTypes: [
        CONTRACT_TYPES.Residential,
        CONTRACT_TYPES.Commercial,
        CONTRACT_TYPES.Industrial,
        CONTRACT_TYPES.Unregulated
      ]
    }
  ].filter(
    tab =>
      !tab.allowedContractTypes ||
      tab.allowedContractTypes.includes(selectedContract.type)
  );

  const renderGraphs = () => {
    if (selectedContract.type !== CONTRACT_TYPES.Unregulated) {
      return (
        <InvoiceConsumptionGraph
          invoiceId={invoices ? invoices[0].id : null}
          displayUnit={
            selectedContract.type === CONTRACT_TYPES.Industrial
              ? DISPLAY_UNIT.volume
              : null
          }
        />
      );
    }

    return (
      <IndustrialInvoiceConsumptionGraph
        contractId={selectedContract.id}
        year={invoices ? invoices[0].year : null}
        month={invoices ? invoices[0].month : null}
      />
    );
  };

  const InvoiceTabs = () => {
    return (
      <div className={classes.tabsStyle}>
        <SimpleTabs
          tabProps={tabProps}
          currentTabIndex={currentTabIndex.checked}
          handleChange={handlerTabChange}
        >
          {renderInvoices()}
          {renderGraphs()}
        </SimpleTabs>
      </div>
    );
  };

  return (
    <>
      <div className={classes.root}>
        <Container maxWidth="md" className={classes.innerRoot}>
          <Typography className={classes.contractsTitle}>
            Información{' '}
            {Company.contractConjugation.regular.singular.contraction}
          </Typography>
          <div className={classes.contractCard}>
            <div className={classes.contractInfo}>
              <Typography className={classes.subtitle}>
                {Company.contractConjugation.capitalized.singular.associate}
              </Typography>
              <Typography className={classes.title}>
                {selectedContract.alias} - {selectedContract.id}
              </Typography>
            </div>
            <div className={classes.paddedContractInfo}>
              <Typography className={classes.subtitle}>
                Nombre del titular
              </Typography>
              <Typography
                className={`${classes.title} ${classes.capitalizeText}`}
              >
                {fullName(
                  selectedContract,
                  'holder.name',
                  'holder.lastName',
                  true
                )}
              </Typography>
            </div>
            <div className={classes.contractInfo}>
              <Typography className={classes.subtitle}>
                Dirección del predio
              </Typography>
              <Typography
                className={`${classes.title} ${classes.capitalizeText}`}
              >
                {formatAddress(selectedContract, true)}
              </Typography>
            </div>
          </div>
          <div className={classes.mainInvoiceTabs}>
            <InvoiceTabs />
          </div>
        </Container>
      </div>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  innerRoot: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: theme.palette.background.cardDark
    },
    paddingBottom: theme.spacing(4)
  },
  contractsTitle: {
    fontSize: 16,
    fontWeight: 500,
    marginTop: theme.spacing(5)
  },
  title: {
    fontSize: 16,
    fontWeight: 500
  },
  capitalizeText: {
    textTransform: 'capitalize'
  },
  subtitle: {
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap'
  },
  contractCard: {
    marginTop: 31,
    border: 'solid 1px #dbdbdb',
    borderRadius: theme.custom.borderRadius,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-around',
    backgroundColor: theme.palette.background.cardDark,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 30,
    paddingBottom: 30,
    [theme.breakpoints.down('xs')]: {
      backgroundColor: theme.palette.background.default,
      flexDirection: 'column',
      alignItems: 'flex-start',
      paddingLeft: 14,
      paddingTop: 24,
      paddingBottom: 32,
      paddingRight: 0,
      boxSizing: 'content-box'
    }
  },
  paddedContractInfo: {
    paddingRight: 12,
    paddingLeft: 12,
    [theme.breakpoints.down('xs')]: {
      paddingRight: 0,
      paddingLeft: 0,
      paddingBottom: 13,
      paddingTop: 13
    }
  },
  mainInvoiceCard: {
    marginTop: theme.spacing(4),
    borderBottom: 'none',
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    [theme.breakpoints.down('xs')]: {
      backgroundColor: theme.palette.background.default
    }
  },
  mainInvoiceCardLoading: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      backgroundColor: theme.palette.background.default
    }
  },
  invoiceCard: {
    backgroundColor: theme.palette.background.default,
    border: 'none',
    [theme.breakpoints.up('sm')]: {
      borderBottom: 'solid 1px #dbdbdb',
      '&:last-child': {
        borderBottom: 'none'
      },
      borderRadius: 0
    },
    [theme.breakpoints.down('xs')]: {
      backgroundColor: theme.palette.background.default,
      border: 'none',
      marginTop: 12
    }
  },
  invoiceActions: {
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    border: 'solid 1px #dbdbdb',
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.only('xs')]: {
      justifyContent: 'flex-end'
    },
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      backgroundColor: theme.palette.background.cardDark
    }
  },
  mainInvoiceTabs: {
    marginTop: theme.spacing(2)
  },
  actionButton: {
    fontWeight: 500,
    fontSize: 12,
    color: theme.palette.common.black,
    textTransform: 'none',
    paddingTop: theme.spacing(1.875),
    paddingBottom: theme.spacing(1.875),
    [theme.breakpoints.up('sm')]: {
      fontSize: 14
    }
  },
  iconRightStyle: {
    transform: 'rotate(180deg)',
    fill: theme.palette.primary.main,
    marginLeft: theme.spacing()
  },
  iconLeftStyle: {
    fill: theme.palette.primary.main,
    marginLeft: theme.spacing()
  },
  invoiceActionsSkeleton: {
    width: '25%'
  },
  noTopBorder: {
    borderTop: 'none'
  }
}));

export default InvoiceHistoryGDC;
